/**
 * @file onboardingSelectors.js
 * @description Who gets the new-user onboarding layer, and when.
 *
 * PURE. No React, no storage, no side effects - so the eligibility matrix (the
 * part that decides whether a paying customer, an admin or a recommender ever
 * sees a first-run modal) is testable without a DOM.
 *
 * DERIVED FLAGS ONLY, NEVER A TIER LITERAL. `user_type` is the billing tier and
 * comparing it here would re-create exactly the class of bug the entitlements
 * rule exists to prevent (CLAUDE.md, "Entitlements are derived, never literal").
 * The three capability flags below are the ones the backend already derives from
 * related-table rows - `is_recommender`, `intervention_enabled`,
 * `has_active_bi_monitor` - plus `status`, which is the trial LIFECYCLE and not a
 * tier: a trialing user is `user_type='solo'` with `status='trial'`.
 *
 * Every predicate FAILS CLOSED on a null/unknown user: an onboarding surface
 * that leaks to someone we know nothing about is worse than one that never shows.
 */

/**
 * The base audience for the whole layer: a trialing customer who is nobody's
 * special case.
 *
 * Dropped here, deliberately:
 *   - admins             - the dashboard is a support tool for them, and
 *                          `/leads/stats` in "all" mode is system-wide, so the
 *                          first-steps card's "contacted" item would be a
 *                          number about everyone.
 *   - recommenders       - including a DUAL-ROLE account (a professional who
 *                          also posts intervention comments). The recommender
 *                          side is onboarded by hand.
 *   - intervention / BI  - both are admin-configured, hand-held accounts.
 *   - anyone not trialing - a paid Solo/Professional/Enterprise user has been
 *                          through this once already, and a lapsed one has a
 *                          subscription banner to read instead.
 *
 * @param {object|null} user - the current user payload (/users/me).
 * @returns {boolean}
 */
export function isOnboardingEligible(user) {
  if (!user) return false;
  if (user.status !== "trial") return false;
  if (user.is_admin || user.is_recommender) return false;
  if (user.intervention_enabled || user.has_active_bi_monitor) return false;
  return true;
}

/**
 * Has the user been matched to at least one profession?
 *
 * `categories` is the matcher's OUTPUT and the only thing the analyzer routes
 * on, which is why it - and not `profession_matching_status` - is the question
 * "can this account receive anything at all".
 *
 * @param {object|null} user
 * @returns {boolean}
 */
export function hasProfession(user) {
  return Boolean(user?.categories?.length);
}

/**
 * Is the user's description sitting in the admin review queue?
 *
 * `needs_review` is the ONLY review status the backend ever writes. `pending`,
 * `pending_review`, `approved` and `rejected` were read by three separate
 * surfaces and are written by nothing - which is why a reviewed user used to
 * see "scanning has started" and "complete your profile" on one screen.
 *
 * @param {object|null} user
 * @returns {boolean}
 */
export function isProfessionInReview(user) {
  return user?.profession_matching_status === "needs_review";
}

/**
 * Statuses that mean "the user may submit a description now".
 *
 * `null` is a user who never submitted; `failed` is one whose Claude call threw
 * (the row is left at `failed` and a resubmit is allowed, at the cost of another
 * daily slot). `matched` / `created` with categories still set are done;
 * `needs_review` is waiting on a human.
 */
const SUBMITTABLE_STATUSES = new Set([null, undefined, "failed"]);

/**
 * Should the welcome dialog open for this user?
 *
 * NO RECENCY GATE, on purpose. A mid-trial user who never set a profession is
 * precisely the leak this layer closes, so they get the dialog once on their
 * next dashboard load rather than never.
 *
 * A `needs_review` user never gets it: there is nothing for them to do, and the
 * empty state already carries that state.
 *
 * @param {object|null} user
 * @returns {boolean}
 */
export function shouldShowWelcome(user) {
  if (!isOnboardingEligible(user)) return false;
  if (user.onboarding_welcome_seen_at) return false;
  if (hasProfession(user)) return false;
  return SUBMITTABLE_STATUSES.has(user.profession_matching_status);
}

/**
 * Is the user in the audience for the first-steps card?
 *
 * The USER half of the rule only. "All three steps are done" needs the
 * contacted count, which is a dashboard figure rather than a user field, so it
 * lives in `components/onboarding/firstStepsModel.js` - keeping this module a
 * function of the user row alone.
 *
 * @param {object|null} user
 * @returns {boolean}
 */
export function isFirstStepsEligible(user) {
  if (!isOnboardingEligible(user)) return false;
  return !user.onboarding_first_steps_dismissed_at;
}

/**
 * Is the "how did you hear about us" question still owed by this user?
 *
 * The SERVER decides the audience (`source_survey_due` on /users/me and
 * /auth/session: new sign-ups only, trialing, never a partner-referred account,
 * never once answered or skipped). This adds the onboarding layer's own
 * audience on top - admins, recommenders, intervention and BI accounts never
 * meet a first-run modal - and reads the flag STRICTLY (`=== true`), so an
 * older payload without the field, or a malformed value, asks nothing.
 *
 * @param {object|null} user
 * @returns {boolean}
 */
export function isSourceSurveyDue(user) {
  if (!isOnboardingEligible(user)) return false;
  return user.source_survey_due === true;
}

/**
 * Should the STANDALONE survey dialog open on this load?
 *
 * It is the fallback for a user who never reached the welcome dialog's survey
 * step - one who closed the welcome early, or an admin-onboarded account that
 * already had a profession and so never met the welcome at all. Its place in
 * the modal queue:
 *
 *   * never in the same session as the welcome dialog or the AUTO-OPENED tour
 *     (one first-run modal per session is the queue's whole rule);
 *   * only once the tour has had its turn - the stored guide version is at or
 *     above the current one (the same signal every later promo waits on);
 *   * never while the welcome dialog itself is still owed - that dialog carries
 *     the survey as its own last step;
 *   * at most once per session, so a failed save does not re-ask on the next
 *     route change.
 *
 * Website / supporter / push / labels promos come AFTER it; that half lives in
 * GuideContext, which tells them to stand down for the session.
 *
 * Every session flag defaults to TRUE (= "already happened") so a caller that
 * forgets one fails CLOSED: the survey stays away rather than stacking on top of
 * something. A missing or non-numeric guide version fails closed the same way.
 *
 * @param {object|null} user
 * @param {object} [session]
 * @param {number} session.currentGuideVersion      - CURRENT_GUIDE_VERSION
 * @param {boolean} [session.welcomeShownThisSession] - welcome took this session
 * @param {boolean} [session.tourShownThisSession]    - tour AUTO-opened this session
 * @param {boolean} [session.surveyShownThisSession]  - survey already asked this session
 * @returns {boolean}
 */
export function shouldShowSourceSurvey(user, session = {}) {
  if (!isSourceSurveyDue(user)) return false;
  const {
    currentGuideVersion,
    welcomeShownThisSession = true,
    tourShownThisSession = true,
    surveyShownThisSession = true,
  } = session;
  if (!Number.isFinite(currentGuideVersion)) return false;
  if ((user.onboarding_guide_version || 0) < currentGuideVersion) return false;
  if (shouldShowWelcome(user)) return false;
  if (welcomeShownThisSession || tourShownThisSession || surveyShownThisSession) {
    return false;
  }
  return true;
}
